// src/pages/ExclusionListPage.tsx
//
// ★ PHASE 6, TASK 23 — Annex A story 32: "Exclusion Lists for eCS Card and
// Foreign ID".
//
// ⚠ THIS SURFACE HOLDS CLASSIFIED PERSONAL DATA (P2C11 §3.6). The list below
// and both downloads show every identifier MASKED to its last 4 characters —
// see `services/administration/src/exclusion-list-repository.ts`'s own
// header for the full reasoning (A-40, SECURITY.md S-030). Searching by the
// EXACT card/ID number (a value the caller already possesses) reveals that
// one row in full; browsing the list never does.
import { plainCode, plainProse } from '../lib/plain-words'
import { CodeBadge } from '../components/StatusBadge'
import { useEffect, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { EXCLUSION_IDENTIFIER_TYPES, type ExclusionIdentifierTypeValue } from '../lib/types'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type ExclusionListSortKey = 'identifierType' | 'exclusionReason' | 'exclusionCriteria' | 'effectiveFrom'

const COLUMN_LABELS: Readonly<Record<ExclusionListSortKey, string>> = {
  identifierType: 'Type',
  exclusionReason: 'Reason',
  exclusionCriteria: 'Criteria',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  identifierValue: 'Identifier (card or ID number)',
  note: 'Note (optional)',
} as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

/**
 * Rows per page. Comfortably under this surface's own `MAX_*_LIMIT` at the
 * gateway, so the server never has to clamp what the screen asked for — a
 * clamped request would silently show fewer rows than the pager's arithmetic
 * assumes and put the page count out by one.
 */
const PAGE_SIZE = 25

export function ExclusionListPage() {
  const { sort, onSort, interacted } = useSort<ExclusionListSortKey>()
  /**
   * ★★ **THE APPLIED search term, and a DRAFT the officer is still typing.**
   *
   * This screen used to send one request PER KEYSTROKE, with a classified
   * personal identifier (an eCS card or Foreign ID number, P2C11 §3.6) in the
   * query string of every one of them —
   * `docs/delivery/DEMO-READINESS-PLAN.md` §Wave 5 lists it. Two things were
   * wrong with that and only one of them is performance:
   *
   *   1. **Fifteen partial copies of a classified identifier travelled that
   *      nobody asked to search for.** Typing a 16-character card number sent
   *      sixteen HTTP requests, each carrying a longer prefix of it in a URL
   *      QUERY STRING — the part of a request that reaches a request log, a
   *      reverse-proxy access log and the browser's own history, and the part
   *      P2C11 §3.13's sanitisation obligation is about. It also wrote sixteen
   *      permanent `audit.event` rows where one was meant.
   *
   *      ⚠ **CORRECTED, and the correction matters:** an earlier draft of this
   *      comment said those audit rows CARRIED the prefixes. They do not.
   *      `audit-hook.ts` records `request.routeOptions.url` — the route
   *      PATTERN — and a `detail` of status, source IP, server host and outcome
   *      reason; checked against real rows in the demonstration database, not
   *      inferred from the code. The rows are noise, not disclosure. The
   *      disclosure is in the request line, which is a different place with
   *      different readers, and the fix is the same either way.
   *   2. **A prefix matches nothing here anyway.** The lookup is EXACT by
   *      design — the row is unmasked only for a caller who already holds the
   *      full identifier — so every intermediate request returned an empty
   *      table, which reads as "not on the list" while the officer is still
   *      typing. The feature actively misinformed until the last keystroke.
   *
   * ⚠ **Debouncing was the obvious fix and is the weaker one:** it sends fewer
   * prefixes, not none. Submitting sends exactly what the officer meant to look
   * up, once.
   */
  const [searchDraft, setSearchDraft] = useState('')
  const [searchValue, setSearchValue] = useState('')
  /**
   * ★★ THE PAGE OFFSET — 2026-08-18. This screen showed the first page of a
   * population it also told the officer the size of, and no control could reach
   * the rest; `services/gateway/src/list-reachability.test.ts` is the census
   * that found it. Held here rather than inside `useOffsetPager` because the
   * FETCH is keyed on it, and the hook is deliberately unaware of fetching.
   */
  const [pagerOffset, setPagerOffset] = useState(0)

  const entries = useApi(
    () =>
      api.exclusionList({
        ...sortParams(sort),
        ...(searchValue.trim().length > 0 && { identifierValue: searchValue.trim() }),
        offset: pagerOffset,
      }),
    [sort.column, sort.dir, searchValue, pagerOffset],
  )

  const [identifierType, setIdentifierType] = useState<ExclusionIdentifierTypeValue>('ECS_CARD')
  const [identifierValue, setIdentifierValue] = useState('')
  const [exclusionReason, setExclusionReason] = useState('')
  const [exclusionCriteria, setExclusionCriteria] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = entries.data?.entries ?? []
  const total = entries.data?.total ?? 0
  const pager = useOffsetPager(PAGE_SIZE, total)

  // ⚠ The pager owns the page NUMBER; this component owns the offset it FETCHES
  // with. Kept in step in one place rather than becoming two sources of one
  // fact — S-011's shape, in a component.
  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])

  // ⚠ A FILTER or SORT change is a different population. Page three of one is
  // not page three of the other, and leaving the offset alone shows an empty
  // table that reads as "no results" when there are plenty.
  useEffect(() => {
    pager.reset()
  }, [sort.column, sort.dir, searchValue])

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = entries.data?.validation
  const problems = boundsProblems(
    validation,
    {
      identifierValue: identifierValue.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
  )

  const canSubmit = identifierValue.trim().length > 0 && effectiveFrom.length > 0 && !problems.any

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). The sentence is composed here, once, from the fields as they were
   * when Save was pressed — never re-derived from a form the officer may since
   * have edited. `danger`, because an exclusion stops a person's bills being
   * processed and a retirement lets them through again.
   *
   * ⚠ The identifier is MASKED to its last four characters in the sentence, as it
   * is in the list and every export (P2C11 §3.6, S-030): the card is an
   * `alertdialog` a screen reader speaks aloud, and the officer typed the full
   * value a moment ago into the field beside it.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const value = identifierValue.trim()
    const masked = value.length > 4 ? `ending ${value.slice(-4)}` : value
    const reason = exclusionReason.trim()
    setConfirming(
      retireInstead
        ? `Retire the ${plainCode(identifierType)} exclusion for the identifier ${masked} from ${effectiveFrom}. No successor opens, so from that day the identifier is re-admitted to processing: a bill carrying it is no longer excluded. There is no undo. A mistake is corrected by another version.`
        : `Exclude the ${plainCode(identifierType)} identifier ${masked} from ${effectiveFrom}${reason.length > 0 ? `: ${reason}` : ''}. From that day every bill carrying it is excluded from processing. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeExclusionListEntry(identifierType, identifierValue.trim(), {
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && {
          exclusionReason: exclusionReason.trim().length > 0 ? exclusionReason.trim() : null,
          exclusionCriteria: exclusionCriteria.trim().length > 0 ? exclusionCriteria.trim() : null,
        }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${plainCode(identifierType)} exclusion retired from ${effectiveFrom}.`
            : `${plainCode(identifierType)} exclusion superseded from ${effectiveFrom}.`,
      })
      setIdentifierValue('')
      setExclusionReason('')
      setExclusionCriteria('')
      setEffectiveFrom('')
      setNote('')
      entries.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this exclusion.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Exclusion list</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The eCS card and Foreign ID exclusion list. <strong>Identifiers are classified
            personal data</strong> and are shown masked to their last 4 characters here and in
            every download, unless you search for the exact card or ID number below: a value you already
            hold. Effective-dated, PSD-only.
          </p>
        </div>
        {/*
          ★★★ WAVE 27 — one control group, owner-directed. See `ExportToolbar`.

          ⚠ THE LABELS KEEP "(masked)" AND THAT IS NOT DECORATION. Every download
          from this screen is masked unless the officer searched for an exact card
          or ID they already hold, and the word is the only thing on the control
          that says so. A tidier "PDF" here would be tidier and less true.

          ⚠ THE EMAILED COPY IS MASKED TOO, because it is the SAME route — the
          email re-invokes the export these two call, so whatever masking that route
          applies travels with the attachment. An exclusion list is the one report
          in this product where an unmasked copy reaching a mailbox would be the
          whole defect.
        */}
        <ExportToolbar
          onDownload={(format) => {
            window.location.href = api.exclusionListExportUrl(format)
          }}
          email={<EmailReportButton path="/api/administration/exclusion-list/export" />}
        />
      </div>

      <div className="pt-card pt-no-print">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event: FormEvent) => {
            event.preventDefault()
            setSearchValue(searchDraft.trim())
          }}
        >
          <label className="flex flex-col gap-1 text-sm max-w-md flex-1 min-w-[16rem]">
            <span className="font-semibold">Search by exact card or ID number</span>
            <input
              type="text"
              className="pt-native-date"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Enter the full identifier, then search"
            />
            <span className="text-xs text-[var(--sgds-body-color-subtle)]">
              The identifier is sent to the server only when you search: it is classified personal data,
              and a partial value matches nothing here in any case: the lookup is exact.
            </span>
          </label>
          <SgdsButton type="submit" variant="primary">
            Search
          </SgdsButton>
          {searchValue.length > 0 && (
            <SgdsButton
              type="button"
              variant="outline"
              onClick={() => {
                setSearchDraft('')
                setSearchValue('')
              }}
            >
              Clear search
            </SgdsButton>
          )}
        </form>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={entries.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {entries.error !== null ? (
        <ErrorCard title="Could not load the exclusion list" message={entries.error} onRetry={entries.reload} />
      ) : entries.initialLoading ? (
        <LoadingCard label="Loading exclusion list" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No exclusions in scope"
          message="Either nothing matches, nothing is currently in force, or your active identity is not PSD: this list is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={entries.loading}>
            <table className="pt-table">
              <caption className="sr-only">
                Exclusion list entries currently in force, identifiers masked to their last four characters
              </caption>
              <thead>
                <tr>
                  <SortableTh label="Type" sortKey="identifierType" sort={sort} onSort={onSort} />
                  {/*
                    ⚠★ THE IDENTIFIER IS THE ONE COLUMN ON THIS SCREEN THAT MUST NOT
                    SORT, and it is a SECURITY decision rather than an omission the
                    header census overlooked.

                    `identifier_value` is classified personal data (P2C11 §3.6) and the
                    cell below masks it to its last four characters. A sort on the full
                    column would let any reader of this screen recover the ORDER of the
                    unmasked values, and from enough page walks their leading
                    characters — a disclosure the masking exists to prevent, reachable
                    without the value ever appearing on screen once. The column is
                    therefore absent from `EXCLUSION_LIST_SORT_COLUMNS` too, so the
                    refusal is at the SERVER and not only here: a hand-written
                    `?sort=identifierValue` degrades to the default order.
                  */}
                  <th
                    scope="col"
                    data-unsortable="classified personal data, masked to its last four characters — sorting the full column would leak the order of the unmasked values, and over enough pages their leading characters"
                  >
                    Identifier
                  </th>
                  <SortableTh label="Reason" sortKey="exclusionReason" sort={sort} onSort={onSort} />
                  <SortableTh label="Criteria" sortKey="exclusionCriteria" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td><CodeBadge value={row.identifierType} /></td>
                    <td className="pt-mono">{row.identifierValue}</td>
                    <td className="text-sm">{row.exclusionReason ?? 'Not recorded'}</td>
                    <td className="text-sm">
                      {row.exclusionCriteria ?? 'Not recorded'}
                      {/*
                        ★★★ WAVE 14 — THE PROVENANCE NOTE, RENDERED, for the reason
                        AuditRetentionPage carries at its own note: the column reached
                        this type and nothing showed it. Here it matters twice over,
                        because the identifier beside it is MASKED — a reader seeing
                        `*************4821` has no way at all to tell a fabricated
                        demonstration entry from a real person's card number, and the
                        masking is what removes the other clue.
                      */}
                      {row.note !== null && (
                        <span className="block text-xs text-[var(--sgds-body-color-subtle)]">{plainProse(row.note)}</span>
                      )}
                    </td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force exclusion{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
          <Pagination pager={pager} label="exclusions" total={total} shown={rows.length} />
        </div>
      )}

      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Establish or retire an exclusion</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            Superseding is the only way an exclusion changes: there is no edit-in-place.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <SgdsSelect
            label="Identifier type"
            value={identifierType}
            onSgdsChange={(e) => setIdentifierType(targetValue(e) as ExclusionIdentifierTypeValue)}
          >
            {EXCLUSION_IDENTIFIER_TYPES.map((t) => (
              <SgdsSelectOption key={t} value={t}>
                {plainCode(t)}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>
          <BoundedField
            label={BOUNDED_FIELDS.identifierValue}
            value={identifierValue}
            onValueChange={setIdentifierValue}
            bounds={validation?.identifierValue}
            problem={problems.of.identifierValue}
            required
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this exclusion instead of superseding it (no successor opens)
          </label>
          {!retireInstead && (
            <>
              <SgdsInput placeholder="" label="Reason (optional)" value={exclusionReason} onSgdsInput={(e) => setExclusionReason(targetValue(e))} />
              <SgdsInput placeholder="" label="Criteria (optional)" value={exclusionCriteria} onSgdsInput={(e) => setExclusionCriteria(targetValue(e))} />
            </>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
            /*
              ★★★ S-086 — THE NOTE IS NOT MASKED, AND THE ONLY PLACE THAT CAN SAY
              SO IS HERE, WHERE IT IS WRITTEN.
              ⚠ The identifier column is masked to its last four characters in
              this list and in every export (A-40 / S-030). `note` is free text
              and is masked NOWHERE — it has always been carried unmasked in the
              export, and since Wave 14 it is rendered in the list beside the
              masked value. So an operator who pastes the card number into a note
              defeats the control for that row, in a spreadsheet that leaves the
              building. Both the reader and the writer are PSD-only, so this
              discloses nothing across a privilege boundary; what it defeats is
              the data-minimisation the mask exists for.
            */
            example="Not masked. The identifier above is shown as its last 4 characters here and in every export - do not repeat the full number in this note."
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              variant="danger"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
